import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { BsArrowRight, BsTwitterX } from "react-icons/bs";
import { FaFacebookF, FaInstagram, FaYoutube, FaCheck, FaTimes } from "react-icons/fa";
import api from "../services/apiService";
import { FaMeta } from "react-icons/fa6";
import Modal from "../components/Modal";

type Platform = "youtube" | "twitter" | "facebook";

interface LoginStatus {
  youtube: boolean;
  twitter: boolean;
  facebook: boolean;
}

function Socialmedia() {
  const { t } = useTranslation();
  const [loginStatus, setLoginStatus] = useState<LoginStatus>({
    youtube: false,
    twitter: false,
    facebook: false
  });
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState<Platform | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [platformToDisconnect, setPlatformToDisconnect] = useState<Platform | null>(null);
  const [isMetaModalOpen, setIsMetaModalOpen] = useState(false);
  const [businessAccount, setBusinessAccount] = useState(false);
  const [connectInstagram, setConnectInstagram] = useState<boolean | null>(null);
  const [step, setStep] = useState(1);

  const userData = JSON.parse(localStorage.getItem("UserData") || "{}");
  const pkg = userData.pkg?.toLowerCase() || "unknown";
  const isTwitterDisabled = pkg === "unknown" || pkg === "free" || pkg === "basic";

  const fetchLoginStatus = async () => {
    try {
      const response = await api.get("/linking/status");

      if (response && typeof response === 'object') {
        const status = response as Partial<LoginStatus>;
        setLoginStatus({
          youtube: Boolean(status.youtube),
          twitter: Boolean(status.twitter),
          facebook: Boolean(status.facebook)
        });
      } else {
        console.error("Invalid response format:", response);
      }
    } catch (error) {
      console.error("Error fetching login status:", error);
    }
  };

  useEffect(() => {
    const initializeStatus = async () => {
      setLoading(true);
      await fetchLoginStatus();
      setLoading(false);
    };

    initializeStatus();
  }, []);

  const handleLogin = (platform: Platform) => {
    if (platform === "twitter" && isTwitterDisabled) {
      return;
    }
    if (platform === "facebook") {
      setIsMetaModalOpen(true);
    } else {
      api.sociallogin(platform);
    }
  };

  const confirmMetaConnect = (instagram: boolean) => {
    api.sociallogin("facebook", {
      business_account: businessAccount,
      instagram: instagram,
    });
    setIsMetaModalOpen(false);
    setStep(1);
    setBusinessAccount(false);
    setConnectInstagram(null);
  };

  const handleLogout = (platform: Platform) => {
    setPlatformToDisconnect(platform);
    setIsModalOpen(true);
  };

  const confirmLogout = async () => {
    if (!platformToDisconnect) return;

    if (loggingOut === platformToDisconnect) {
      return;
    }
    setLoggingOut(platformToDisconnect);

    try {
      const response = await api.get(`/linking/${platformToDisconnect}/logout`);

      if (response) {
        await fetchLoginStatus();
        console.log(`Successfully logged out from ${platformToDisconnect}`);
      } else {
        const status = response && typeof response === "object" && "status" in response ? (response as any).status : "unknown";
        const statusText = response && typeof response === "object" && "statusText" in response ? (response as any).statusText : "";
        console.error(`Error logging out from ${platformToDisconnect}:`, status, statusText);
      }
    } catch (error: any) {
      console.error(`Error logging out from ${platformToDisconnect}:`, error.response?.data || error.message);
    } finally {
      setLoggingOut(null);
      setIsModalOpen(false);
      setPlatformToDisconnect(null);
    }
  };


  // Loading state
  if (loading) {
    return (
      <div className="my-4">
        <h1 className="pl-2 font-bold text-xl">{t("socialmedia.socialMediaLinkingTitle")}</h1>
        <div className="flex gap-6 mt-6 xs:max-sm:flex-col">
          {[1, 2, 3].map((index) => (
            <div key={index} className="p-6 max-w-[350px] rounded-3xl shadow bg-white">
              <div className="animate-pulse">
                <div className="h-12 bg-gray-200 rounded-2xl mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="my-4">
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        label={t("socialmedia.disconnectModal.title")}
      >
        <div className="text-center">
          <p>{t("socialmedia.disconnectModal.message", { platform: platformToDisconnect })}</p>
          <div className="flex justify-center gap-4 mt-4">
            <button
              onClick={() => setIsModalOpen(false)}
              className="py-2 px-4 bg-gray-300 text-black rounded-lg"
            >
              {t("socialmedia.disconnectModal.noButton")}
            </button>
            <button
              onClick={confirmLogout}
              className="py-2 px-4 bg-red-500 text-white rounded-lg"
            >
              {t("socialmedia.disconnectModal.yesButton")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isMetaModalOpen}
        onClose={() => {
          setIsMetaModalOpen(false);
          setStep(1);
          setBusinessAccount(false);
          setConnectInstagram(null);
        }}
        label={t("socialmedia.metaConnection.title")}
      >
        <div className="text-center">
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
            <div
              className="bg-blue-500 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${(step / 2) * 100}%` }}
            ></div>
          </div>
          {step === 1 && (
            <div className="mb-4 transition-opacity duration-500 ease-in-out opacity-100">
              <p className="text-lg flex items-center justify-center gap-2">
                <FaFacebookF color="#1877F2" /> {t("socialmedia.metaConnection.businessAccountQuestion")}
              </p>
              <div className="flex justify-center gap-4 mt-4">
                <button
                  onClick={() => {
                    setBusinessAccount(true);
                    setStep(2);
                  }}
                  className="py-2 px-6 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors cursor-pointer flex items-center gap-2"
                >
                  <FaCheck color="green" /> {t("socialmedia.metaConnection.yesButton")}
                </button>
                <button
                  onClick={() => {
                    setBusinessAccount(false);
                    setStep(2);
                  }}
                  className="py-2 px-6 rounded-lg bg-gray-300 text-black hover:bg-gray-400 transition-colors cursor-pointer flex items-center gap-2"
                >
                  <FaTimes color="red" /> {t("socialmedia.metaConnection.noButton")}
                </button>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="mb-4 transition-opacity duration-500 ease-in-out opacity-100">
              <p className="text-lg flex items-center justify-center gap-2">
                <FaInstagram color="#E4405F" /> {t("socialmedia.metaConnection.instagramQuestion")}
              </p>
              <div className="flex justify-center gap-4 mt-4">
                <button
                  onClick={() => confirmMetaConnect(true)}
                  className={`py-2 px-6 rounded-lg ${connectInstagram === true ? "bg-blue-500 text-white" : "bg-gray-300 text-black hover:bg-gray-400"} transition-colors cursor-pointer flex items-center gap-2`}
                >
                  <FaCheck color="green" /> {t("socialmedia.metaConnection.yesButton")}
                </button>
                <button
                  onClick={() => confirmMetaConnect(false)}
                  className={`py-2 px-6 rounded-lg ${connectInstagram === false ? "bg-blue-500 text-white" : "bg-gray-300 text-black hover:bg-gray-400"} transition-colors cursor-pointer flex items-center gap-2`}
                >
                  <FaTimes color="red" /> {t("socialmedia.metaConnection.noButton")}
                </button>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center mt-6">
            <div>
              {step === 2 && (
                <button
                  onClick={() => setStep(1)}
                  className="py-2 px-6 bg-gray-200 text-black rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t("socialmedia.metaConnection.backButton", "Back")}
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <h1 className="pl-2 font-bold text-xl">{t("socialmedia.socialMediaLinkingTitle")}</h1>
      <div className="flex gap-6 mt-6 xs:max-sm:flex-col">

        {/* YouTube Login/Logout */}
        <div className="p-6 flex-col xs:max-sm:max-w-[400px] xs:max-sm:mx-auto xs:max-sm:py-8 xs:max-sm:px-4 max-w-[350px] items-center rounded-3xl shadow flex bg-white">
          <button
            className={`flex items-center text-[1rem] justify-between rounded-2xl w-full py-2.5 px-3 cursor-pointer transition-colors duration-200 ${loginStatus.youtube
              ? "bg-[#F2000026] text-[#C70000] hover:bg-[#F2000040]"
              : "bg-[#F200000D] text-[#F20000] hover:bg-[#F2000020]"
              }`}
            onClick={() => loginStatus.youtube ? handleLogout("youtube") : handleLogin("youtube")}
            disabled={loggingOut === "youtube"}
          >
            <span className="flex items-center gap-2">
              <span className={`p-2 rounded-full ${loginStatus.youtube ? "bg-[#C70000]" : "bg-[#F20000]"}`}>
                <FaYoutube size={24} className="text-white" />
              </span>
              {loggingOut === "youtube"
                ? t("socialmedia.loggingOut", "Logging out...")
                : loginStatus.youtube
                  ? t("socialmedia.youtubeLogoutButton")
                  : t("socialmedia.youtubeLoginButton")
              }
            </span>
            <BsArrowRight
              size={24}
              className={`${loginStatus.youtube ? "text-[#C70000]" : "text-[#F20000]"} ${loggingOut === "youtube" ? "animate-spin" : ""
                }`}
            />
          </button>
          <p className="mt-4 text-[#4B5563]">
            <strong className="text-black mr-2">{t("socialmedia.permissions")}</strong>
            {t("socialmedia.youtubePermissions")}
          </p>
        </div>

        {/* Twitter (X) Login/Logout */}
        <div className="p-6 flex-col xs:max-sm:max-w-[400px] xs:max-sm:mx-auto xs:max-sm:py-8 xs:max-sm:px-4 max-w-[350px] items-center rounded-3xl shadow flex bg-white">
          <button
            className={`flex items-center text-[1rem] justify-between rounded-2xl w-full py-2.5 px-3 transition-colors duration-200 ${isTwitterDisabled
              ? "bg-gray-300 text-gray-400 cursor-not-allowed"
              : loginStatus.twitter
                ? "bg-[#00000026] text-[#333333] cursor-pointer hover:bg-[#00000040]"
                : "bg-[#00000012] text-[#000000] cursor-pointer hover:bg-[#00000020]"
              }`}
            disabled={isTwitterDisabled || loggingOut === "twitter"}
            onClick={() => {
              if (!isTwitterDisabled) {
                loginStatus.twitter ? handleLogout("twitter") : handleLogin("twitter");
              }
            }}
            title={isTwitterDisabled ? "This feature is not available for your plan" : ""}
          >
            <span className="flex items-center gap-2">
              <span className={`p-2 rounded-full ${isTwitterDisabled
                ? "bg-gray-400"
                : loginStatus.twitter
                  ? "bg-[#333333]"
                  : "bg-[#000000]"
                }`}>
                <BsTwitterX size={24} className="text-white" />
              </span>
              {isTwitterDisabled
                ? t("socialmedia.xLoginButton")
                : loggingOut === "twitter"
                  ? t("socialmedia.loggingOut", "Logging out...")
                  : loginStatus.twitter
                    ? t("socialmedia.xLogoutButton")
                    : t("socialmedia.xLoginButton")
              }
            </span>
            <BsArrowRight
              size={24}
              className={`${isTwitterDisabled
                ? "text-gray-400"
                : loginStatus.twitter
                  ? "text-[#333333]"
                  : "text-[#000000]"
                } ${loggingOut === "twitter" ? "animate-spin" : ""}`}
            />
          </button>
          <p className="mt-4 text-[#4B5563]">
            <strong className="text-[#000000] mr-2">{t("socialmedia.permissions")}</strong>
            {t("socialmedia.xPermissions")}
          </p>
        </div>

        {/* Facebook Login/Logout */}
        <div className="p-6 flex-col xs:max-sm:max-w-[400px] xs:max-sm:mx-auto xs:max-sm:py-8 xs:max-sm:px-4 max-w-[350px] items-center rounded-3xl shadow flex bg-white">
          <button
            className={`flex items-center text-[1rem] justify-between rounded-2xl w-full py-2.5 px-3 cursor-pointer transition-colors duration-200 ${loginStatus.facebook
              ? "bg-[#3A559F36] text-[#2B4177] hover:bg-[#3A559F50]"
              : "bg-[#3A559F21] text-[#3A559F] hover:bg-[#3A559F30]"
              }`}
            onClick={() => loginStatus.facebook ? handleLogout("facebook") : handleLogin("facebook")}
            disabled={loggingOut === "facebook"}
          >
            <span className="flex items-center gap-2">
              <span className={`p-2 rounded-full ${loginStatus.facebook ? "bg-[#2B4177]" : "bg-[#3A559F]"}`}>
                <FaMeta size={24} className="text-white" />
              </span>
              {loggingOut === "facebook"
                ? t("socialmedia.loggingOut", "Logging out...")
                : loginStatus.facebook
                  ? t("socialmedia.facebookLogoutButton")
                  : t("socialmedia.facebookLoginButton")
              }
            </span>
            <BsArrowRight
              size={24}
              className={`${loginStatus.facebook ? "text-[#2B4177]" : "text-[#3A559F]"} ${loggingOut === "facebook" ? "animate-spin" : ""
                }`}
            />
          </button>
          <p className="mt-4 text-[#4B5563]">
            <strong className="text-black mr-2">{t("socialmedia.permissions")}</strong>
            {t("socialmedia.facebookPermissions")}
            <span className="flex items-center gap-2 ml-2">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1877F2]">
                <FaFacebookF size={18} className="text-white" />
              </span>
              <span
                className="w-8 h-8 flex items-center justify-center rounded-full text-white"
                style={{
                  background: "linear-gradient(45deg, #feda75, #fa7e1e, #d62976, #962fbf, #4f5bd5)"
                }}
              >
                <FaInstagram size={18} />
              </span>
            </span>


          </p>
        </div>
      </div>
    </div>
  );
}

export default Socialmedia;
